// libraries
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import noop from 'lodash/noop'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useForm, FormProvider } from 'react-hook-form'
import { Stack, Grid, Box, Divider, Typography, Button } from '@mui/material'
import AddIcon from '@mui/icons-material/AddCircleOutline'
import { randomId } from '@mui/x-data-grid-generator'

// queries
import {
  useCreateRegradeMutation,
  useUpdateRegradeMutation,
  useGetRegradeByIdQuery,
  useVoidRegradeMutation,
} from 'services/regrades'

// state
import { setDelayRefetch } from 'state/inventory'

// components
import RegradeMaterialRow from 'components/containers/inventory/regrades/RegradeMaterialRowLegacy'
import Input from 'components/generic/form/Input'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import FormModalActionBar from 'components/generic/modals/form-modal/FormModalActionBar'
import Loading from 'components/generic/Loading'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import DateTimePicker from 'components/generic/form/DateTimePicker'

// helpers and utils
import { formOptions } from 'utils/validation/regradesLegacy'
import { MODAL_VIEW_MODES, UUIDRegex, WASTE_CODE, CHURN_ZERO_EVENTS as cz } from 'utils/constants'
import { trackEvent } from 'utils/tracking'
import { setDecimalPrecision, dispatchSnackbar } from 'utils/helpers'
import { formatDataLegacy, getNewMaterial, getToValues } from 'components/containers/inventory/regrades/utils'
import FORM_MODALS from 'utils/ui/modals'
import { showFormModal } from 'state/ui'

const NewRegradeContainerLegacy = ({ onFormStateChange, onHideModal }) => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const methods = useForm(formOptions)
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
    setError,
    clearErrors,
    getValues,
  } = methods

  const { viewMode, navigateTo, initialMaterialTypeId, initialEffectiveDate, initialNewWeight } = useSelector(
    state => state.ui.formModalProps,
  )
  const { defaultYardTimezone } = useSelector(state => state.auth)

  const isNew = viewMode === MODAL_VIEW_MODES.NEW
  const regradeId = useMemo(() => {
    const cleanedPathname = pathname.at(-1) === '/' ? pathname.slice(0, -1) : pathname
    return isNew ? null : cleanedPathname.split('/').pop()
  }, [pathname])

  const [toMaterials, setToMaterials] = useState(initialMaterialTypeId ? [] : [getNewMaterial()])
  const [showVoidModal, setShowVoidModal] = useState(false)
  const [valueTransferred, setValueTransferred] = useState(0)
  const [rowValues, setRowValues] = useState({})
  const setFormattedValueTransferred = value => {
    const valueAmount = `$${setDecimalPrecision(value, { decimalPrecisionOverride: 2 })}`
    setValueTransferred(valueAmount)
  }

  const { data: regradeData, isLoading: isLoadingRegradeData } = useGetRegradeByIdQuery(
    { regradeId },
    {
      refetchOnMountOrArgChange: true,
      skip: !regradeId && !UUIDRegex.test(regradeId),
    },
  )
  const readableRegradeId = regradeData.regradeNumber
  const title = regradeId ? `Regrade #${readableRegradeId}` : 'New Regrade'

  const [createRegrade, { isLoading: isLoadingCreateRegrade }] = useCreateRegradeMutation()
  const [updateRegrade, { isLoading: isLoadingUpdateRegrade }] = useUpdateRegradeMutation()
  const [voidRegrade, { isLoading: isDeletingRegrade }] = useVoidRegradeMutation()

  useEffect(() => {
    const { value: fromValue, quantity: fromQuantity } = rowValues?.from || { quantity: 0 }
    const tosValue = Object.keys(rowValues)
      .filter(key => key !== 'from')
      .reduce((sum, key) => sum + rowValues[key]?.value, 0)

    const toQuantity = Object.keys(rowValues)
      .filter(key => key !== 'from')
      .reduce((sum, key) => sum + parseInt(rowValues[key]?.quantity || 0, 10), 0)
    setFormattedValueTransferred(tosValue - fromValue)
    if (parseInt(fromQuantity, 10) < parseInt(toQuantity, 10)) {
      setError('isTransferQuantityValid', { message: 'To material quantity cannot exceed from material quantity.' })
    } else {
      clearErrors('isTransferQuantityValid')
    }
  }, [rowValues])

  useEffect(() => {
    if (isNew && initialMaterialTypeId && initialEffectiveDate && initialNewWeight) {
      const resetRegradeObject = {
        from: {
          material: '',
          quantity: '',
        },
        notes: '',
        effectiveDate: dayjs(initialEffectiveDate),
      }
      const newToMaterials = [
        {
          ...getNewMaterial(),
          quantity: initialNewWeight,
        },
      ]
      setToMaterials(newToMaterials)
      resetRegradeObject[newToMaterials[0].id] = {
        material: { id: initialMaterialTypeId, materialTypeId: initialMaterialTypeId },
        quantity: initialNewWeight,
      }
      reset(resetRegradeObject)
    }
  }, [initialMaterialTypeId, initialEffectiveDate, initialNewWeight])

  useEffect(() => {
    if (!regradeData) return
    setFormattedValueTransferred(regradeData?.valueTransferred)
    const resetRegradeObject = {
      from: {
        material: regradeData?.fromMaterial?.materialType,
        quantity: regradeData?.fromMaterial?.weight,
      },
      notes: regradeData?.notes,
      effectiveDate: dayjs(regradeData?.effectiveDate),
    }

    const nonWasteToMaterials =
      regradeData?.toMaterials
        ?.filter(material => material.materialType && material.materialType.code !== WASTE_CODE)
        .map(material => ({
          ...material,
          id: randomId(),
        })) || []
    setToMaterials(nonWasteToMaterials)
    nonWasteToMaterials.forEach(material => {
      resetRegradeObject[material.id] = {
        material: material?.materialType,
        quantity: material.weight,
      }
    })

    reset(resetRegradeObject)
  }, [regradeData])

  const isLoading = isLoadingRegradeData || isLoadingCreateRegrade || isLoadingUpdateRegrade || isDeletingRegrade

  const onModalClose = useCallback(() => {
    onHideModal()
    if (navigateTo) {
      navigate(navigateTo)
    }
  })

  const getCzEventName = isVoid => {
    if (isVoid) return cz.VOID_REGRADE
    if (viewMode === MODAL_VIEW_MODES.NEW) return cz.CREATE_REGRADE
    if (viewMode === MODAL_VIEW_MODES.EDIT) return cz.UPDATE_REGRADE
    return cz.REGRADE_MATERIALS
  }

  const handleSuccess = (response, action) => {
    const isVoid = action === 'voided'
    trackEvent({
      eventName: getCzEventName(isVoid),
      ...(!isVoid && { quantity: response?.toMaterials?.length }),
    })
    dispatchSnackbar(dispatch, 'success', `Regrade successfully ${action}`)
    setTimeout(() => {
      dispatch(setDelayRefetch(false))
    }, 3000)
    onModalClose()
  }

  const handleError = (error, action) => {
    trackEvent({
      eventName: getCzEventName(action === 'voiding'),
      errorMsg: error,
    })
    dispatchSnackbar(dispatch, 'error', error?.data?.error || `There was an error ${action} the regrade`)
    dispatch(setDelayRefetch(false))
  }

  const onClickAddMaterial = () => {
    const newToMaterials = [...toMaterials, getNewMaterial()]
    setToMaterials(newToMaterials)
  }

  const onClickDeleteMaterial = material => {
    const newToMaterials = toMaterials.filter(m => m.id !== material.id)
    setToMaterials(newToMaterials)
    setRowValues(prevRows => {
      const newRows = { ...prevRows }
      delete newRows[material?.id]
      return newRows
    })
  }

  const areValidToMaterials = data => {
    const toValues = getToValues(data, toMaterials)
    const lessOrEqualToZeroToMaterials = toValues.some(material => material.weight <= 0)
    if (!lessOrEqualToZeroToMaterials) return true
    // TODO: fix set this error to the correct field
    setError('isTransferQuantityValid', { message: 'Material quantity should be greater than 0.' })
    return false
  }

  const someEmptyToMaterials = () => {
    const data = getValues()
    const toValues = getToValues(data, toMaterials)
    return toValues.some(material => !material.materialTypeId || material.weight === '')
  }

  const onSubmit = async data => {
    if (!data.from?.material?.materialTypeId) {
      setError('from', { message: 'From material is required' })
      return
    }
    if (!areValidToMaterials(data)) return
    const regrade = formatDataLegacy(data, toMaterials)
    dispatch(setDelayRefetch(true))
    if (viewMode === MODAL_VIEW_MODES.NEW) {
      await createRegrade({ regradeDto: regrade })
        .unwrap()
        .then(response => handleSuccess(response, 'created'))
        .catch(error => handleError(error, 'creating'))
    } else {
      await updateRegrade({ regradeId, regradeDto: { ...regrade, id: regradeId } })
        .unwrap()
        .then(response => handleSuccess(response, 'updated'))
        .catch(error => handleError(error, 'updating'))
    }
  }

  const onClickVoid = async () => {
    setShowVoidModal(false)

    await voidRegrade({ regradeId })
      .unwrap()
      .then(response => handleSuccess(response, 'voided'))
      .catch(error => handleError(error, 'voiding'))
  }

  useEffect(() => {
    onFormStateChange(false)
  }, [])

  const onEdit = () => {
    dispatch(
      showFormModal({
        formModalName: FORM_MODALS.NEW_REGRADE,
        formModalProps: {
          viewMode: MODAL_VIEW_MODES.EDIT,
          navigateTo: '/app/inventory/regrades',
        },
      }),
    )
  }

  return (
    <FormProvider {...methods}>
      <AlertModal
        open={showVoidModal}
        modalType="void"
        title="Void This Regrade"
        question="Are you sure you want to void this regrade?"
        description={`Regrade #${readableRegradeId} will be voided and all data will be lost.`}
        confirmLabel="YES, CONFIRM VOID"
        cancelLabel="GO BACK"
        onClose={() => {
          setShowVoidModal(false)
        }}
        onSubmit={onClickVoid}
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        {isLoading && <Loading />}
        <Stack height="100vh">
          <FormModalHeading title={title} backButtonPosition="right" onBack={onModalClose} />
          <Divider />
          <Stack alignItems="center">
            <Box width="20%" />
          </Stack>
          <Stack p={5} flexGrow={1} overflow="scroll">
            <Box mb={3}>
              <Typography variant="h6" mb={3}>
                Effective Date:
              </Typography>
              <Grid container spacing={2}>
                <Grid item md={4} xs={12}>
                  <DateTimePicker
                    name="effectiveDate"
                    label="Effective Date"
                    control={control}
                    variant="outlined"
                    defaultDateTime={dayjs()}
                    dateTimePickerProps={{
                      disabled: viewMode === MODAL_VIEW_MODES.VIEW,
                      size: 'small',
                      'data-test': 'regrade-effective-datetime-picker',
                      timezone: defaultYardTimezone,
                    }}
                    error={!!errors?.effectiveDate}
                    helperText={errors?.effectiveDate?.message}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box>
              <Typography variant="h6">From:</Typography>

              <RegradeMaterialRow
                name="from"
                disabled={viewMode === MODAL_VIEW_MODES.VIEW}
                setRowValues={setRowValues}
              />
            </Box>

            <Box mt={3}>
              <Typography variant="h6">To:</Typography>

              {toMaterials.map(material => (
                <RegradeMaterialRow
                  key={material.id}
                  name={material.id}
                  disabled={viewMode === MODAL_VIEW_MODES.VIEW}
                  deleteDisabled={viewMode === MODAL_VIEW_MODES.VIEW || toMaterials.length === 1}
                  onClickDelete={() => onClickDeleteMaterial(material)}
                  setRowValues={setRowValues}
                />
              ))}
            </Box>
            {viewMode !== MODAL_VIEW_MODES.VIEW ? (
              <Stack mt={2} mr="auto" direction="column">
                <Button
                  variant="outlined"
                  endIcon={<AddIcon />}
                  onClick={onClickAddMaterial}
                  sx={{ width: 'fit-content' }}
                >
                  Add Material
                </Button>
                {errors?.isTransferQuantityValid?.message ? (
                  <Typography color="error" mt={2}>
                    {errors?.isTransferQuantityValid?.message}
                  </Typography>
                ) : null}
              </Stack>
            ) : null}
            <Box mt={5} mb={3}>
              <Stack container="true" mt={1} spacing={2}>
                <Typography variant="h6">Additional Information</Typography>
                <Stack>
                  <Typography variant="body1">Value transferred</Typography>
                  <Typography variant="body1">{valueTransferred}</Typography>
                </Stack>
              </Stack>
              <Grid container mt={1} spacing={2}>
                <Grid item xs={12}>
                  <Input
                    name="notes"
                    label="Notes"
                    data-test="notes"
                    variant="outlined"
                    defaultValue=""
                    control={control}
                    disabled={viewMode === MODAL_VIEW_MODES.VIEW}
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>

          <FormModalActionBar
            submitActionText={viewMode === MODAL_VIEW_MODES.EDIT ? 'EDIT' : 'Confirm Regrade'}
            submitDisabled={!isValid || someEmptyToMaterials() || !!errors?.isTransferQuantityValid}
            viewMode={viewMode}
            deleteActionText="Delete"
            onCancel={onModalClose}
            onDelete={() => {
              setShowVoidModal(true)
            }}
            onEdit={onEdit}
            isFormVoidable
            isFormEditable
          />
        </Stack>
      </form>
    </FormProvider>
  )
}

NewRegradeContainerLegacy.propTypes = {
  onFormStateChange: PropTypes.func,
  onHideModal: PropTypes.func,
}

NewRegradeContainerLegacy.defaultProps = {
  onFormStateChange: noop,
  onHideModal: null,
}

export default NewRegradeContainerLegacy
